import { Flex, FlexAlignCenter, FlexAlignCenterSpaceBetween, ItemFlex1 } from 'components/primitives/flex';
import { Text } from 'components/primitives/typography';
import dayjs from 'dayjs';
import { useAtom, useSetAtom } from 'jotai';
import styled from 'styled-components';
import snakeCase from 'lodash/snakeCase';
import InstantBookingIcon from 'assets/images/icons/instant-booking.svg';
import ManagerIcon from 'assets/images/icons/manager.svg';
import { useEffect, useState } from 'react';
import { useBreakpoint } from 'components/BreakpointProvider';
import CaretRightIcon from 'assets/scss/fortawesome/icons/svg/caret-right.svg';
import { useUserDetails } from 'auth/mutations/userHooks';
import Avatar from 'components/ui/Avatar/Avatar';
import BookingBadge from 'components/ui/BookingBadge/BookingBadge';
import { determineStaticPreviewImage } from 'utils/images';
import useReadArchiveMessageMutation from './hooks/useReadArchiveMessageMutation';
import useConversationDetail from './hooks/useConversationDetail';
import { Loader } from './commons';
import { activeConversationIdAtom, mobileConversationDetailVisibleAtom } from './store';

export default function ConversationItem({
  conversation: {
    read,
    conversation_id,
    message,
    booking: { trip_finish, trip_start, id: bookingId, boat, managed_by_boat_manager, instant_book, state },
    state_read_by_user,
    my_user_type_in_conversation,
    other_party,
    booking_modification,
  },
}) {
  const setMobileDetailVisible = useSetAtom(mobileConversationDetailVisibleAtom);
  const [activeConversationId, setActiveConversationId] = useAtom(activeConversationIdAtom);
  const patchMessageMutation = useReadArchiveMessageMutation(activeConversationId, bookingId);
  const [clickedForDetail, setClickedForDetail] = useState(false);
  const { isLoading } = useConversationDetail();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const { userId, userDetails } = useUserDetails();
  const impersonated = userDetails.is_impersonating;

  const getDisplayDate = () => {
    const tripStart = dayjs(trip_start).tz(boat?.time_zone);
    const tripFinish = dayjs(trip_finish).tz(boat?.time_zone);

    // eslint-disable-next-line no-nested-ternary
    return tripStart.isSame(tripFinish, 'day')
      ? tripStart.format('MMM DD, YYYY')
      : tripStart.isSame(tripFinish, 'month')
      ? `${dayjs(trip_start).format('MMM DD')} - ${dayjs(trip_finish).format('DD, YYYY')}`
      : `${dayjs(trip_start).format('MMM DD')} - ${dayjs(trip_finish).format('MMM DD, YYYY')}`;
  };

  const hasManager =
    my_user_type_in_conversation === 'renter' || my_user_type_in_conversation === 'captain'
      ? false
      : managed_by_boat_manager;

  useEffect(() => {
    if (!isLoading && clickedForDetail) {
      setMobileDetailVisible(true);
      setClickedForDetail(false);
    }
  }, [isLoading, clickedForDetail]);

  let messageToShow;
  if (state === 'expired' || state === 'declined') {
    messageToShow =
      my_user_type_in_conversation === 'renter' ? `Request ${state}. See your options!` : `Request ${state}.`;
  } else {
    messageToShow = message?.body;
  }

  const textColor = read ? '#5E696A' : '#0A252F';
  return (
    <Item
      data-testid={`conversation-item-${conversation_id}`}
      isUnread={!read}
      active={conversation_id === activeConversationId}
      onClick={() => {
        setActiveConversationId(conversation_id);
        setClickedForDetail(true);
        if (!read && !impersonated) {
          patchMessageMutation.mutate({});
        }
      }}
    >
      <Flex gap="12px">
        <Avatar
          diameter={48}
          profileLink={`/users/${other_party.id}`}
          src={determineStaticPreviewImage(other_party.picture.url, other_party.first_name)}
        />
        <ItemFlex1>
          <FlexAlignCenterSpaceBetween>
            <Text fSize="15px" fWeight="600" color={textColor}>
              {other_party.first_name}
            </Text>
            <FlexAlignCenter gap="4px">
              <Text fSize="12px" fWeight="400" color={textColor}>
                {dayjs(message?.created_at).format('h:mm A')}
              </Text>
              <CaretRightIcon width={10} />
            </FlexAlignCenter>
          </FlexAlignCenterSpaceBetween>
          <Preview fSize="14px" mt="2px" mb="6px" color={textColor}>
            {message?.sender?.id === userId ? 'You' : message?.sender?.first_name}: {messageToShow}
          </Preview>
          <FlexAlignCenterSpaceBetween>
            <Text fSize="12px" fWeight="400" color={textColor}>
              Trip: {getDisplayDate()}
            </Text>

            <FlexAlignCenter gap="4px">
              {hasManager && <ManagerIcon data-testid="inbox-manager-icon" />}
              {instant_book && <InstantBookingIcon width={16} data-testid="inbox-instantbook-icon" />}
              <BookingBadge
                bookingStatus={snakeCase(state_read_by_user)}
                bookingModification={booking_modification?.can_accept_or_decline}
              />
            </FlexAlignCenter>
          </FlexAlignCenterSpaceBetween>
        </ItemFlex1>
      </Flex>

      {smallScreen && isLoading && clickedForDetail && <Loader />}
    </Item>
  );
}

export const renderConversationItem = (conversation) => (
  <ConversationItem key={conversation.conversation_id} conversation={conversation} />
);

const Item = styled.div<{ isUnread: boolean; active: boolean }>`
  border-top: 1px solid transparent;
  border-bottom: 1px solid #f2f2f2;
  cursor: pointer;
  position: relative;
  padding: 26px 24px;

  ${({ isUnread }) =>
    isUnread &&
    `
   &::before {
     content: "";
     width: 8px;
     height: 8px;
     background: #0751C2;
     display: block;
     border-radius: 50%;
     position: absolute;
     left: 8px;
     top: 46px;
   }
  `}

  ${({ active = true }) =>
    active &&
    `
    border-top-color: #BDE3FD;
    border-bottom: 1px solid  #BDE3FD;
    background: #FAFDFF;
  `};
`;

const Preview = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;
